/**
 * One People ticket, opened from Ticket Management. The same "each control commits on change"
 * shape as the BP Tickets dialog (TicketDetailModal), plus what the merged table adds: the queue,
 * the category-specific details, the timestamps, and two notes — one the employee sees in My
 * Inquiries (Resolution Note) and one they never do (Internal Notes, stripped by the proxy).
 */
import { useState } from 'react';
import { Check, MessageSquare, Paperclip } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { fetchPeopleTicketSlackContact } from '../../services/peopleTicketsApi';
import type { PeopleTicket, PeopleTicketOptions, TicketPerson, TicketPriority, TicketQueue, TicketStatus } from '../../types/peopleTickets';
import { StatusSelect } from '../ticketing/StatusSelect';
import { PrioritySelect } from '../ticketing/PrioritySelect';
import { AssigneeSelect } from '../ticketing/AssigneeSelect';
import { SELECT_CHEVRON } from '../ticketing/chevron';
import { nameFromEmail } from '../ticketing/format';
import { buildTicketOutreachMessage, slackDmUrl } from '../ticketing/slackOutreach';
import { queueLabel, resolutionTimeLabel } from './ticketList';

const SEP = '::';

/** Category picker grouped by queue: moving a ticket to another team IS picking a category there. */
function QueueCategorySelect({
  ticket,
  options,
  onChange,
}: {
  ticket: PeopleTicket;
  options: PeopleTicketOptions;
  onChange: (queue: TicketQueue, category: string) => void;
}) {
  const current = ticket.queue && ticket.category ? `${ticket.queue}${SEP}${ticket.category}` : '';
  // A ticket filed before its category moved queue (BP · Workation Request, Oct 29 2026) has a
  // pair the options no longer list; without its own option the browser shows the first one.
  const listed = Boolean(ticket.queue && ticket.category && options.queueCategories[ticket.queue]?.includes(ticket.category));
  return (
    <select
      aria-label="Queue and category"
      value={current}
      onChange={(e) => {
        const [queue, category] = e.target.value.split(SEP);
        if (queue && category) onChange(queue as TicketQueue, category);
      }}
      style={{ backgroundImage: SELECT_CHEVRON, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '10px' }}
      className="appearance-none rounded bg-pearl py-0.5 pl-2 pr-6 body-12-regular text-iron"
    >
      {!current && <option value="">No category</option>}
      {current && !listed && ticket.queue && (
        <option value={current} disabled>
          {`${ticket.category} (${queueLabel(ticket.queue)})`}
        </option>
      )}
      {options.queues.map((q) => (
        <optgroup key={q} label={queueLabel(q)}>
          {(options.queueCategories[q] ?? []).map((c) => (
            <option key={c} value={`${q}${SEP}${c}`}>
              {c}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/**
 * A note with its own, always-visible Save button (David, Oct 1 2026): without one it looked as
 * if text typed here went nowhere. Save is disabled until something changed, and says "Saved"
 * once the proxy has confirmed the write — not when the click happened.
 */
function NoteField({
  label,
  hint,
  value,
  onSave,
}: {
  label: string;
  hint: string;
  value: string;
  onSave: (next: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const dirty = draft !== value;

  async function save() {
    if (saving || !dirty) return;
    setSaving(true);
    try {
      await onSave(draft);
      setSavedAt(Date.now());
    } catch {
      // The list already showed a toast and rolled the change back; keep the draft so nothing is lost.
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {/* Same spacing as the request form: the design-system type classes trim their own line
          boxes, so the visible gap is smaller than the margin. */}
      <p className="mb-2.5 body-14-semibold text-black">{label}</p>
      <p className="mb-3 body-12-regular text-iron">{hint}</p>
      <Textarea
        aria-label={label}
        rows={3}
        value={draft}
        className="rounded"
        textAreaClassName="body-14-regular"
        onChange={(e) => {
          setDraft(e.target.value);
          setSavedAt(null);
        }}
      />
      <div className="mt-2 flex items-center justify-end gap-3">
        {savedAt && !dirty && (
          <span className="inline-flex items-center gap-1 body-12-regular text-green before:hidden after:hidden" role="status">
            <Check className="h-3.5 w-3.5" aria-hidden="true" /> Saved
          </span>
        )}
        <Button size="sm" variant="outline" className="rounded" loading={saving} disabled={!dirty || saving} onClick={save}>
          Save {label.toLowerCase()}
        </Button>
      </div>
    </div>
  );
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

export function PeopleTicketDialog({
  ticket,
  options,
  assigneeOptions,
  onClose,
  onStatusChange,
  onPriorityChange,
  onCategoryChange,
  onAssigneeChange,
  onNoteSave,
}: {
  ticket: PeopleTicket;
  options: PeopleTicketOptions;
  assigneeOptions: TicketPerson[];
  onClose: () => void;
  onStatusChange: (status: TicketStatus) => void;
  onPriorityChange: (priority: TicketPriority) => void;
  onCategoryChange: (queue: TicketQueue, category: string) => void;
  onAssigneeChange: (assignee: TicketPerson) => void;
  onNoteSave: (field: 'resolutionNote' | 'internalNotes', value: string) => Promise<void>;
}) {
  const [slackLoading, setSlackLoading] = useState(false);

  // Same order of operations as TicketDetailModal, for the same browser reasons: clipboard while
  // the click still owns focus, then open the tab synchronously, then navigate it after the lookup.
  async function handleMessageOnSlack() {
    setSlackLoading(true);
    let slackTab: Window | null = null;
    try {
      await navigator.clipboard.writeText(buildTicketOutreachMessage(ticket));
      slackTab = window.open('', '_blank');
      const { slackUserId } = await fetchPeopleTicketSlackContact(ticket.id);
      if (slackTab) {
        slackTab.location.href = slackDmUrl(slackUserId);
        slackTab.opener = null;
        showToast({ status: 'success', description: 'Drafted message copied — paste it into the Slack DM that just opened.' });
      } else {
        showToast({ status: 'info', description: 'Drafted message copied, but your browser blocked the new tab — open Slack yourself and paste it in.' });
      }
    } catch (err) {
      slackTab?.close();
      showToast({ status: 'error', description: err instanceof Error ? err.message : 'Could not open Slack' });
    } finally {
      setSlackLoading(false);
    }
  }

  const resolved = resolutionTimeLabel(ticket.businessDaysToResolve);
  const details: [string, string][] = [
    ['Submitted by', nameFromEmail(ticket.submittedBy)],
    ['Department', ticket.department || '—'],
    ['Submitted', when(ticket.submittedAt)],
    ['Resolved', ticket.resolvedAt ? `${when(ticket.resolvedAt)}${resolved ? ` · ${resolved}` : ''}` : '—'],
  ];
  if (ticket.subcategory) details.push(['Detail', ticket.subcategory]);
  if (ticket.location) details.push(['Location', ticket.location]);
  if (ticket.restockReason) details.push(['Reason', ticket.restockReason]);
  if (ticket.legacyId) details.push(['Old ticket', ticket.legacyId]);

  return (
    <Modal open onOpenChange={(next: boolean) => !next && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-4rem)] !w-[min(100vw-2rem,36rem)] gap-2 overflow-auto rounded p-0">
        <ModalHeader className="border-b border-solid border-pearl px-6 py-5">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
              <span className="body-12-regular text-iron">
                {ticket.ticketKey}
                {ticket.queue ? ` · ${queueLabel(ticket.queue)}` : ''}
              </span>
              <StatusSelect status={ticket.status} onChange={onStatusChange} />
            </div>
            {/* Same `!` overrides as TicketDetailModal: ModalTitle's page-header size dominates a dialog. */}
            <ModalTitle className="!mt-0 !text-2xl !font-semibold !leading-snug !text-black">{ticket.summary}</ModalTitle>
          </div>
        </ModalHeader>

        <div className="space-y-6 px-6 py-5">
          {ticket.description && (
            <p className="whitespace-pre-wrap rounded bg-cotton px-4 py-3 body-14-regular text-black">{ticket.description}</p>
          )}

          {ticket.attachments.length > 0 && (
            <ul className="space-y-1">
              {ticket.attachments.map((a) => (
                <li key={a.id || a.url}>
                  <a href={a.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 body-14-regular text-trustedBlue hover:underline before:hidden after:hidden">
                    <Paperclip className="h-4 w-4" />
                    {a.filename}
                  </a>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap gap-2">
            <PrioritySelect priority={ticket.priority} onChange={onPriorityChange} />
            <QueueCategorySelect ticket={ticket} options={options} onChange={onCategoryChange} />
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-5">
            <div>
              <dt className="body-12-regular text-iron">Assigned to</dt>
              <dd className="mt-2 body-14-regular">
                <AssigneeSelect dri={ticket.assignee} options={assigneeOptions} onChange={onAssigneeChange} />
              </dd>
            </div>
            {details.map(([label, value]) => (
              <div key={label}>
                <dt className="body-12-regular text-iron">{label}</dt>
                <dd className="mt-2 body-14-regular text-black">{value}</dd>
              </div>
            ))}
          </dl>

          <NoteField
            key={`res-${ticket.id}`}
            label="Resolution note"
            hint={`${nameFromEmail(ticket.submittedBy)} sees this in My inquiries.`}
            value={ticket.resolutionNote}
            onSave={(v) => onNoteSave('resolutionNote', v)}
          />
          <NoteField
            key={`int-${ticket.id}`}
            label="Internal notes"
            hint="Only the People team sees this."
            value={ticket.internalNotes ?? ''}
            onSave={(v) => onNoteSave('internalNotes', v)}
          />
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-solid border-pearl px-6 py-4">
          {ticket.slackSource ? (
            <a href={ticket.slackSource} target="_blank" rel="noreferrer" className="body-12-regular text-trustedBlue hover:underline">
              View original Slack message →
            </a>
          ) : (
            <span />
          )}
          <Button variant="outline" size="sm" className="w-fit shrink-0 rounded" contentClassName="gap-3" onClick={handleMessageOnSlack} disabled={slackLoading}>
            <MessageSquare className="h-4 w-4" />
            {slackLoading ? 'Looking up on Slack…' : 'Message on Slack'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
